/**
 * „Note pentru EA" pe o plata eMAG (13.10.2026): incasarile pe facturile DSMF si compensarea
 * facturilor eMAG, ca fisier de import in ExpertAccounts. Vezi `emagNoteEa.service.ts` in API.
 *
 * Intai previzualizarea — intreaba EA ce facturi mai sunt deschise, deci poate dura — si abia
 * apoi fisierul. Ce nu intra in fisier se vede aici, cu motivul, nu se pierde pe tacute.
 */
import { useState } from 'react'
import { api } from '../../lib/api'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { useAuthStore } from '../../stores/auth'
import { useConfirmare } from '../../components/ui/Confirmare'

interface Previzualizare {
  payoutId: string
  payoutDate: string
  eaExportatLa: string | null
  incasari: { n: number; suma: number }
  restituiri: { n: number; suma: number }
  facturiEmag: { n: number; suma: number }
  note: number
  control: { totalPlata: number; pe5125: number; exclus: number; diferenta: number }
  excluse: { cod: string; ref: string | null; suma: number; motiv: string }[]
}

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Mesajul serverului, si cand raspunsul a venit ca blob (descarcarea). */
async function mesaj(e: unknown, implicit: string): Promise<string> {
  const d = (e as { response?: { data?: unknown } })?.response?.data
  if (d instanceof Blob) {
    try {
      const j = JSON.parse(await d.text()) as { error?: { message?: string } }
      return j.error?.message ?? implicit
    } catch {
      return implicit
    }
  }
  const o = d as { error?: { message?: string }; message?: string } | undefined
  return o?.error?.message ?? o?.message ?? (e instanceof Error ? e.message : implicit)
}

export function NoteEaPlata({ payoutId }: { payoutId: string }) {
  const poate = useAuthStore((s) => s.permissions).includes('emag:write')
  const [p, setP] = useState<Previzualizare | null>(null)
  const [lucreaza, setLucreaza] = useState<'verific' | 'descarc' | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const { confirma } = useConfirmare()
  if (!poate) return null

  const verifica = async () => {
    setLucreaza('verific')
    setEroare(null)
    try {
      setP((await api.get(`/emag/payouts/${payoutId}/note-ea`, { timeout: 4 * 60_000 })).data)
    } catch (e) {
      setEroare(await mesaj(e, 'Nu am putut pregăti notele.'))
    } finally {
      setLucreaza(null)
    }
  }
  const descarca = async () => {
    if (
      p?.eaExportatLa &&
      !(await confirma({
        titlu: 'Fișierul acestei plăți a mai fost descărcat',
        mesaj: `Prima dată pe ${new Date(p.eaExportatLa).toLocaleString('ro-RO')}. Dacă l-ai importat deja în EA, un al doilea import dublează încasările.`,
        buton: 'Descarcă totuși',
        pericol: true,
      }))
    ) {
      return
    }
    setLucreaza('descarc')
    setEroare(null)
    try {
      await downloadXlsx(
        `/emag/payouts/${payoutId}/note-ea/fisier`,
        `EA note eMag ${payoutId}.xlsx`,
      )
      await verifica()
    } catch (e) {
      setEroare(await mesaj(e, 'Fișierul nu s-a putut face.'))
    } finally {
      setLucreaza(null)
    }
  }

  // Excluse grupate pe motiv: sute de randuri cu acelasi motiv nu se citesc unul cate unul.
  const peMotiv = new Map<string, { n: number; suma: number; exemple: string[] }>()
  for (const e of p?.excluse ?? []) {
    const cheie = e.motiv.replace(/DSMF\d+/g, 'DSMF…').replace(/[-\d.,]+ lei/g, '… lei')
    const g = peMotiv.get(cheie) ?? { n: 0, suma: 0, exemple: [] }
    g.n++
    g.suma += e.suma
    if (g.exemple.length < 5 && e.ref) g.exemple.push(`${e.cod} ${e.ref}`)
    peMotiv.set(cheie, g)
  }

  return (
    <div className="rounded-md border border-b4l-border bg-b4l-bg-soft p-3 text-[11px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">Note pentru ExpertAccounts</span>
        {p?.eaExportatLa && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">
            descărcat pe {new Date(p.eaExportatLa).toLocaleString('ro-RO')}
          </span>
        )}
        <span className="ml-auto flex gap-2">
          <button
            onClick={() => void verifica()}
            disabled={lucreaza != null}
            className="rounded-md border border-b4l-border bg-white px-3 py-1 font-semibold shadow-sm hover:border-b4l-blue hover:text-b4l-blue-dark disabled:opacity-60"
          >
            {lucreaza === 'verific'
              ? 'Se verifică în EA…'
              : p
                ? 'Verifică din nou'
                : 'Pregătește notele'}
          </button>
          {p && p.note > 0 && p.control.diferenta === 0 && (
            <button
              onClick={() => void descarca()}
              disabled={lucreaza != null}
              className="rounded-md bg-b4l-blue px-3 py-1 font-semibold text-white shadow-sm hover:brightness-110 disabled:opacity-60"
            >
              {lucreaza === 'descarc' ? 'Se face fișierul…' : 'Descarcă fișierul pentru EA'}
            </button>
          )}
        </span>
      </div>
      {lucreaza === 'verific' && (
        <p className="mt-1 text-b4l-text-muted">
          Se verifică în EA facturile deschise — poate dura până la două minute.
        </p>
      )}
      {eroare && <p className="mt-1 text-b4l-red-dark">{eroare}</p>}
      {p && (
        <div className="mt-2 grid gap-2">
          <table className="w-auto">
            <tbody>
              <tr>
                <td className="pr-4">Încasări pe facturi (5125.eMag = 4111)</td>
                <td className="pr-2 text-right tabular-nums">{p.incasari.n}</td>
                <td className="text-right tabular-nums">{lei(p.incasari.suma)}</td>
              </tr>
              <tr>
                <td className="pr-4">Restituiri pe stornări (sumă negativă)</td>
                <td className="pr-2 text-right tabular-nums">{p.restituiri.n}</td>
                <td className="text-right tabular-nums">{lei(p.restituiri.suma)}</td>
              </tr>
              <tr>
                <td className="pr-4">Facturi eMAG compensate (401.Dante = 5125.eMag)</td>
                <td className="pr-2 text-right tabular-nums">{p.facturiEmag.n}</td>
                <td className="text-right tabular-nums">{lei(p.facturiEmag.suma)}</td>
              </tr>
              <tr className="border-t border-b4l-border font-semibold">
                <td className="pr-4">Pe 5125.eMag din note / total plată</td>
                <td />
                <td className="text-right tabular-nums">
                  {lei(p.control.pe5125)} / {lei(p.control.totalPlata)}
                </td>
              </tr>
              <tr>
                <td className="pr-4">Rămas în afara fișierului</td>
                <td className="pr-2 text-right tabular-nums">{p.excluse.length}</td>
                <td className="text-right tabular-nums">{lei(p.control.exclus)}</td>
              </tr>
            </tbody>
          </table>
          {p.control.diferenta !== 0 && (
            <p className="text-b4l-red-dark">
              Notele nu se închid pe total plată (diferență {lei(p.control.diferenta)}). Nu se face
              fișierul — spune-i lui Alexandru.
            </p>
          )}
          {peMotiv.size > 0 && (
            <div>
              <p className="font-semibold">Ce nu intră în fișier (se rezolvă de mână):</p>
              <ul className="list-disc pl-5">
                {[...peMotiv].map(([motiv, g]) => (
                  <li key={motiv}>
                    {g.n} × {motiv} ({lei(g.suma)} lei)
                    {g.exemple.length > 0 && (
                      <span className="text-b4l-text-muted"> — ex.: {g.exemple.join(', ')}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-b4l-text-muted">
                Lista completă e și în fișier, în foaia „Excluse”.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
